import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import VenueCard from "@/components/VenueCard";
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Filter } from "lucide-react";

export default function SEOLandingPage() {
    const loc = useLocation();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setData(null); setNotFound(false);
        api.get(`/seo/page`, { params: { slug: loc.pathname } })
            .then((r) => setData(r.data))
            .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
    }, [loc.pathname]);

    const schema = data ? [
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" },
                { "@type": "ListItem", position: 2, name: data.h1, item: window.location.href },
            ],
        },
        data.faq?.length ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: data.faq.map((f) => ({
                "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        } : null,
    ].filter(Boolean) : null;

    useSEO({
        title: data?.seo_title || "Venues | Venuely",
        description: data?.meta_description || "",
        noindex: data?.index_status === "noindex",
        canonical: data ? window.location.origin + data.slug : undefined,
        schema,
    });

    if (notFound) return <div className="container-app py-24 text-center"><h1 className="font-display text-4xl">Page not found</h1><Link to="/" className="mt-4 inline-block text-primary">Back home →</Link></div>;
    if (!data) return <div className="container-app py-16"><div className="h-8 w-1/2 bg-muted/50 rounded animate-pulse mb-8" /><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{Array.from({length:6}).map((_,i)=><div key={i} className="rounded-xl bg-muted/40 aspect-[4/3] animate-pulse" />)}</div></div>;

    const bc = data.slug.split("/").filter(Boolean);
    return (
        <div className="container-app py-8 md:py-12">
            <Breadcrumb className="mb-4">
                <BreadcrumbList>
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
                    {bc.map((p, i) => (
                        <React.Fragment key={i}>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>{i === bc.length - 1 ? <BreadcrumbPage>{p.replace(/-/g, " ")}</BreadcrumbPage> : p.replace(/-/g, " ")}</BreadcrumbItem>
                        </React.Fragment>
                    ))}
                </BreadcrumbList>
            </Breadcrumb>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] max-w-3xl" data-testid="seo-h1">{data.h1}</h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{data.intro_copy}</p>

            <div className="mt-10 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{data.venues?.length || 0} venue{(data.venues?.length || 0) === 1 ? "" : "s"} available</p>
                <Link to={`/search?${new URLSearchParams({ ...(data.city ? { city: data.city } : {}), ...(data.venue_type ? { venue_type: data.venue_type } : {}), ...(data.event_type ? { event_type: data.event_type } : {}) }).toString()}`}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="seo-refine">
                    <Filter className="h-4 w-4" /> Refine search
                </Link>
            </div>

            {(data.venues?.length || 0) > 0 ? (
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="seo-venue-grid">
                    {data.venues.map((v, i) => <VenueCard key={v.venue_id} venue={v} index={i} />)}
                </div>
            ) : (
                <div className="mt-4 rounded-xl border border-dashed border-border p-12 text-center bg-muted/20">
                    <p className="font-display text-2xl">We're growing our directory here.</p>
                    <p className="text-muted-foreground mt-2">Own or manage a venue that fits? <Link to="/list-your-venue" className="text-primary underline">List it free</Link>.</p>
                </div>
            )}

            {data.faq?.length > 0 && (
                <div className="mt-16">
                    <h2 className="font-display text-2xl md:text-3xl font-semibold mb-4">Frequently asked</h2>
                    <Accordion type="single" collapsible className="border border-border rounded-xl divide-y divide-border">
                        {data.faq.map((f, i) => (
                            <AccordionItem key={i} value={`i${i}`} className="px-4 border-0">
                                <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
                                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            )}

            {data.related_links?.length > 0 && (
                <div className="mt-16">
                    <h2 className="font-display text-2xl md:text-3xl font-semibold mb-4">Explore related searches</h2>
                    <div className="flex flex-wrap gap-2">
                        {data.related_links.map((r, i) => (
                            <Link key={i} to={r.href} className="rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-muted transition-colors">{r.label}</Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
